import { useState, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  PageContainer,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useApiClient,
  useOrganization,
  Alert,
  AlertDescription,
  Badge,
} from '@startupquickstart/frontend';
import { JsonViewer } from '@/components/shared/JsonViewer';
import {
  GreaterAreaDropdown,
  AttractionCategoryDropdown,
  AttractionTypeDropdown,
  AttractionDropdown,
  TourTypeDropdown,
  TourDropdown,
} from '@/components/dropdowns';
import { Play, Loader2, Copy, Check } from 'lucide-react';

type EndpointType = 'list' | 'detail' | 'tours-list' | 'tours-detail';

interface ApiResponse {
  data: unknown;
  status: number;
  timing: number;
  url: string;
}

/**
 * API Explorer page for testing mobile API endpoints
 * Internal tool for viewing API responses
 */
function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [value]);

  return (
    <Button variant="ghost" size="sm" onClick={handleCopy} className="h-7 px-2">
      {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

export function ApiExplorerPage() {
  const apiClient = useApiClient();
  const { getToken } = useAuth();
  const { dbOrganization } = useOrganization();
  const [activeTab, setActiveTab] = useState<EndpointType>('list');
  const [tokenCopied, setTokenCopied] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<ApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // List endpoint parameters
  const [languageCode, setLanguageCode] = useState('en');
  const [greaterAreaId, setGreaterAreaId] = useState('');
  const [attractionCategoryId, setAttractionCategoryId] = useState('');
  const [attractionTypeId, setAttractionTypeId] = useState('');
  const [userLatitude, setUserLatitude] = useState('');
  const [userLongitude, setUserLongitude] = useState('');
  const [search, setSearch] = useState('');
  const [themeTypeSlug, setThemeTypeSlug] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState('1');
  const [limit, setLimit] = useState('10');
  const [orderBy, setOrderBy] = useState('');
  const [orderDirection, setOrderDirection] = useState('');

  // Detail endpoint parameters
  const [attractionId, setAttractionId] = useState('');
  const [detailLanguageCode, setDetailLanguageCode] = useState('en');
  const [detailThemeTypeSlug, setDetailThemeTypeSlug] = useState('');

  // Tours list endpoint parameters
  const [toursLanguageCode, setToursLanguageCode] = useState('en');
  const [toursGreaterAreaId, setToursGreaterAreaId] = useState('');
  const [tourTypeId, setTourTypeId] = useState('');
  const [toursSearch, setToursSearch] = useState('');
  const [toursStatus, setToursStatus] = useState('');
  const [toursPage, setToursPage] = useState('1');
  const [toursLimit, setToursLimit] = useState('10');
  const [toursOrderBy, setToursOrderBy] = useState('');
  const [toursOrderDirection, setToursOrderDirection] = useState('');

  // Tours detail endpoint parameters
  const [tourId, setTourId] = useState('');
  const [tourDetailLanguageCode, setTourDetailLanguageCode] = useState('en');
  const [tourDetailThemeTypeSlug, setTourDetailThemeTypeSlug] = useState('');

  const copyToken = useCallback(async () => {
    setTokenLoading(true);
    try {
      const t = await getToken();
      if (t) {
        await navigator.clipboard.writeText(t);
        setTokenCopied(true);
        setTimeout(() => setTokenCopied(false), 2000);
      }
    } finally {
      setTokenLoading(false);
    }
  }, [getToken]);

  const executeListRequest = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const params = new URLSearchParams();
      if (greaterAreaId) params.append('greaterAreaId', greaterAreaId);
      if (attractionCategoryId) params.append('attractionCategoryId', attractionCategoryId);
      if (attractionTypeId) params.append('attractionTypeId', attractionTypeId);
      if (userLatitude) params.append('userLatitude', userLatitude);
      if (userLongitude) params.append('userLongitude', userLongitude);
      if (search) params.append('search', search);
      if (themeTypeSlug) params.append('themeTypeSlug', themeTypeSlug);
      if (status) params.append('status', status);
      if (page) params.append('page', page);
      if (limit) params.append('limit', limit);
      if (orderBy) params.append('orderBy', orderBy);
      if (orderDirection) params.append('orderDirection', orderDirection);

      const queryString = params.toString();
      const endpoint = `/${languageCode}/attractions${queryString ? `?${queryString}` : ''}`;

      const startTime = performance.now();
      const data = await apiClient.get(endpoint);
      const endTime = performance.now();

      setResponse({
        data,
        status: 200,
        timing: Math.round(endTime - startTime),
        url: `/api/v1${endpoint}`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const executeDetailRequest = async () => {
    if (!attractionId) {
      setError('Attraction ID is required for detail endpoint');
      return;
    }

    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const params = new URLSearchParams();
      if (detailThemeTypeSlug) params.append('themeTypeSlug', detailThemeTypeSlug);

      const queryString = params.toString();
      const endpoint = `/${detailLanguageCode}/attractions/${attractionId}${queryString ? `?${queryString}` : ''}`;

      const startTime = performance.now();
      const data = await apiClient.get(endpoint);
      const endTime = performance.now();

      setResponse({
        data,
        status: 200,
        timing: Math.round(endTime - startTime),
        url: `/api/v1${endpoint}`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const executeToursListRequest = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const params = new URLSearchParams();
      if (toursGreaterAreaId) params.append('greaterAreaId', toursGreaterAreaId);
      if (tourTypeId) params.append('tourTypeId', tourTypeId);
      if (toursSearch) params.append('search', toursSearch);
      if (toursStatus) params.append('status', toursStatus);
      if (toursPage) params.append('page', toursPage);
      if (toursLimit) params.append('limit', toursLimit);
      if (toursOrderBy) params.append('orderBy', toursOrderBy);
      if (toursOrderDirection) params.append('orderDirection', toursOrderDirection);

      const queryString = params.toString();
      const endpoint = `/${toursLanguageCode}/tours${queryString ? `?${queryString}` : ''}`;

      const startTime = performance.now();
      const data = await apiClient.get(endpoint);
      const endTime = performance.now();

      setResponse({
        data,
        status: 200,
        timing: Math.round(endTime - startTime),
        url: `/api/v1${endpoint}`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const executeTourDetailRequest = async () => {
    if (!tourId) {
      setError('Tour ID is required for detail endpoint');
      return;
    }

    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const params = new URLSearchParams();
      if (tourDetailThemeTypeSlug) params.append('themeTypeSlug', tourDetailThemeTypeSlug);

      const queryString = params.toString();
      const endpoint = `/${tourDetailLanguageCode}/tours/${tourId}${queryString ? `?${queryString}` : ''}`;

      const startTime = performance.now();
      const data = await apiClient.get(endpoint);
      const endTime = performance.now();

      setResponse({
        data,
        status: 200,
        timing: Math.round(endTime - startTime),
        url: `/api/v1${endpoint}`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">API Explorer</h1>
          <p className="text-muted-foreground mt-2">
            Test mobile API endpoints and view formatted responses
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Auth Context</CardTitle>
            <CardDescription>
              Organization ID and Clerk JWT for testing with curl or external tools
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Organization ID</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-muted px-3 py-1.5 text-sm font-mono truncate">
                  {dbOrganization?.id || 'No organization selected'}
                </code>
                {dbOrganization?.id && <CopyButton value={dbOrganization.id} />}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Bearer Token</Label>
              <div>
                <Button variant="outline" size="sm" onClick={copyToken} disabled={tokenLoading}>
                  {tokenLoading ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  ) : tokenCopied ? (
                    <Check className="mr-2 h-3.5 w-3.5 text-green-500" />
                  ) : (
                    <Copy className="mr-2 h-3.5 w-3.5" />
                  )}
                  {tokenCopied ? 'Copied' : 'Copy Token'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as EndpointType)}>
          <TabsList>
            <TabsTrigger value="list">List Attractions</TabsTrigger>
            <TabsTrigger value="detail">Get Attraction Detail</TabsTrigger>
            <TabsTrigger value="tours-list">List Tours</TabsTrigger>
            <TabsTrigger value="tours-detail">Get Tour Detail</TabsTrigger>
          </TabsList>

          <TabsContent value="list" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>GET /api/v1/:languageCode/attractions</CardTitle>
                <CardDescription>
                  Search and filter attractions with pagination
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Required Parameters */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold">Required Parameters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="languageCode">Language Code</Label>
                      <Input
                        id="languageCode"
                        value={languageCode}
                        onChange={(e) => setLanguageCode(e.target.value)}
                        placeholder="en"
                      />
                    </div>
                  </div>
                </div>

                {/* Filtering Parameters */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold">Filtering Parameters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="greaterAreaId">Greater Area</Label>
                      <GreaterAreaDropdown
                        value={greaterAreaId}
                        onChange={setGreaterAreaId}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="attractionCategoryId">Category</Label>
                      <AttractionCategoryDropdown
                        value={attractionCategoryId}
                        onChange={setAttractionCategoryId}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="attractionTypeId">Type</Label>
                      <AttractionTypeDropdown
                        value={attractionTypeId}
                        onChange={setAttractionTypeId}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="themeTypeSlug">Theme Type Slug</Label>
                      <Input
                        id="themeTypeSlug"
                        value={themeTypeSlug}
                        onChange={(e) => setThemeTypeSlug(e.target.value)}
                        placeholder="e.g., family-friendly"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="search">Search Text</Label>
                      <Input
                        id="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search attractions..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger id="status">
                          <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="published">Published</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Location Parameters */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold">Location Parameters (for distance sorting)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="userLatitude">User Latitude</Label>
                      <Input
                        id="userLatitude"
                        value={userLatitude}
                        onChange={(e) => setUserLatitude(e.target.value)}
                        placeholder="e.g., 37.7749"
                        type="number"
                        step="any"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="userLongitude">User Longitude</Label>
                      <Input
                        id="userLongitude"
                        value={userLongitude}
                        onChange={(e) => setUserLongitude(e.target.value)}
                        placeholder="e.g., -122.4194"
                        type="number"
                        step="any"
                      />
                    </div>
                  </div>
                </div>

                {/* Pagination & Sorting */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold">Pagination & Sorting</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="page">Page</Label>
                      <Input
                        id="page"
                        value={page}
                        onChange={(e) => setPage(e.target.value)}
                        type="number"
                        min="1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="limit">Limit</Label>
                      <Input
                        id="limit"
                        value={limit}
                        onChange={(e) => setLimit(e.target.value)}
                        type="number"
                        min="1"
                        max="100"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="orderBy">Order By</Label>
                      <Select value={orderBy} onValueChange={setOrderBy}>
                        <SelectTrigger id="orderBy">
                          <SelectValue placeholder="Default" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="name">Name</SelectItem>
                          <SelectItem value="createdAt">Created At</SelectItem>
                          <SelectItem value="updatedAt">Updated At</SelectItem>
                          <SelectItem value="distance">Distance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="orderDirection">Order Direction</Label>
                      <Select value={orderDirection} onValueChange={setOrderDirection}>
                        <SelectTrigger id="orderDirection">
                          <SelectValue placeholder="Default" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="asc">Ascending</SelectItem>
                          <SelectItem value="desc">Descending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <Button onClick={executeListRequest} disabled={loading} className="w-full">
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Executing...
                      </>
                    ) : (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        Execute Request
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="detail" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>GET /api/v1/:languageCode/attractions/:id</CardTitle>
                <CardDescription>
                  Get a single attraction with full details including featured image, gallery, and voiceovers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold">Required Parameters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="detailLanguageCode">Language Code</Label>
                      <Input
                        id="detailLanguageCode"
                        value={detailLanguageCode}
                        onChange={(e) => setDetailLanguageCode(e.target.value)}
                        placeholder="en"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="attractionId">Attraction</Label>
                      <AttractionDropdown
                        value={attractionId}
                        onChange={setAttractionId}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold">Optional Parameters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="detailThemeTypeSlug">Theme Type Slug</Label>
                      <Input
                        id="detailThemeTypeSlug"
                        value={detailThemeTypeSlug}
                        onChange={(e) => setDetailThemeTypeSlug(e.target.value)}
                        placeholder="e.g., family-friendly"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <Button onClick={executeDetailRequest} disabled={loading} className="w-full">
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Executing...
                      </>
                    ) : (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        Execute Request
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tours-list" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>GET /api/v1/:languageCode/tours</CardTitle>
                <CardDescription>
                  Search and filter tours with pagination and language support
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Required Parameters */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold">Required Parameters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="toursLanguageCode">Language Code</Label>
                      <Input
                        id="toursLanguageCode"
                        value={toursLanguageCode}
                        onChange={(e) => setToursLanguageCode(e.target.value)}
                        placeholder="en"
                      />
                    </div>
                  </div>
                </div>

                {/* Filtering Parameters */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold">Filtering Parameters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="toursGreaterAreaId">Greater Area</Label>
                      <GreaterAreaDropdown
                        value={toursGreaterAreaId}
                        onChange={setToursGreaterAreaId}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tourTypeId">Tour Type</Label>
                      <TourTypeDropdown
                        value={tourTypeId}
                        onChange={setTourTypeId}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="toursSearch">Search Text</Label>
                      <Input
                        id="toursSearch"
                        value={toursSearch}
                        onChange={(e) => setToursSearch(e.target.value)}
                        placeholder="Search tours..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="toursStatus">Status</Label>
                      <Select value={toursStatus} onValueChange={setToursStatus}>
                        <SelectTrigger id="toursStatus">
                          <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="published">Published</SelectItem>
                          <SelectItem value="needs_review">Needs Review</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Pagination & Sorting */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold">Pagination & Sorting</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="toursPage">Page</Label>
                      <Input
                        id="toursPage"
                        value={toursPage}
                        onChange={(e) => setToursPage(e.target.value)}
                        type="number"
                        min="1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="toursLimit">Limit</Label>
                      <Input
                        id="toursLimit"
                        value={toursLimit}
                        onChange={(e) => setToursLimit(e.target.value)}
                        type="number"
                        min="1"
                        max="100"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="toursOrderBy">Order By</Label>
                      <Select value={toursOrderBy} onValueChange={setToursOrderBy}>
                        <SelectTrigger id="toursOrderBy">
                          <SelectValue placeholder="Default" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="name">Name</SelectItem>
                          <SelectItem value="duration">Duration</SelectItem>
                          <SelectItem value="stops">Stops</SelectItem>
                          <SelectItem value="createdAt">Created At</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="toursOrderDirection">Order Direction</Label>
                      <Select value={toursOrderDirection} onValueChange={setToursOrderDirection}>
                        <SelectTrigger id="toursOrderDirection">
                          <SelectValue placeholder="Default" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="asc">Ascending</SelectItem>
                          <SelectItem value="desc">Descending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <Button onClick={executeToursListRequest} disabled={loading} className="w-full">
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Executing...
                      </>
                    ) : (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        Execute Request
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tours-detail" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>GET /api/v1/:languageCode/tours/:id</CardTitle>
                <CardDescription>
                  Get a single tour with full details including attractions list
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold">Required Parameters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="tourDetailLanguageCode">Language Code</Label>
                      <Input
                        id="tourDetailLanguageCode"
                        value={tourDetailLanguageCode}
                        onChange={(e) => setTourDetailLanguageCode(e.target.value)}
                        placeholder="en"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tourId">Tour</Label>
                      <TourDropdown
                        value={tourId}
                        onChange={setTourId}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold">Optional Parameters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="tourDetailThemeTypeSlug">Theme Type Slug</Label>
                      <Input
                        id="tourDetailThemeTypeSlug"
                        value={tourDetailThemeTypeSlug}
                        onChange={(e) => setTourDetailThemeTypeSlug(e.target.value)}
                        placeholder="e.g., family-friendly"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <Button onClick={executeTourDetailRequest} disabled={loading} className="w-full">
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Executing...
                      </>
                    ) : (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        Execute Request
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Response Display */}
        {(response || error) && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Response</CardTitle>
                {response && (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{response.timing}ms</Badge>
                    <Badge variant={response.status === 200 ? 'default' : 'destructive'}>
                      {response.status}
                    </Badge>
                  </div>
                )}
              </div>
              {response && (
                <CardDescription className="font-mono text-xs">
                  GET {response.url}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {response && <JsonViewer data={response.data} />}
            </CardContent>
          </Card>
        )}
      </div>
    </PageContainer>
  );
}
